import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import webhookService from "../../../lib/webhookService";
import { useAuth } from "../../../contexts/AuthContext";

const WebhookTestModal = ({ isOpen, onClose, webhook, availableEvents }) => {
  const { user } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState("");
  const [customPayload, setCustomPayload] = useState("");
  const [useCustomPayload, setUseCustomPayload] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const samplePayloads = {
    "message.sent": {
      event: "message.sent",
      timestamp: "2024-01-20T12:35:00Z",
      data: {
        message_id: "msg_12345",
        recipient: "+1234567890",
        content: "Hello, this is a test message!",
        channel: "sms",
        status: "sent",
        sent_at: "2024-01-20T12:35:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "message.delivered": {
      event: "message.delivered",
      timestamp: "2024-01-20T12:36:00Z",
      data: {
        message_id: "msg_12345",
        recipient: "+1234567890",
        status: "delivered",
        delivered_at: "2024-01-20T12:36:00Z",
        carrier: "Verizon",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "message.read": {
      event: "message.read",
      timestamp: "2024-01-20T12:37:00Z",
      data: {
        message_id: "msg_12345",
        recipient: "+1234567890",
        status: "read",
        read_at: "2024-01-20T12:37:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "message.failed": {
      event: "message.failed",
      timestamp: "2024-01-20T12:35:00Z",
      data: {
        message_id: "msg_12345",
        recipient: "+1234567890",
        content: "Hello, this is a test message!",
        channel: "sms",
        status: "failed",
        error: {
          code: "INVALID_NUMBER",
          message: "The recipient phone number is invalid",
        },
        failed_at: "2024-01-20T12:35:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "user.joined": {
      event: "user.joined",
      timestamp: "2024-01-20T12:30:00Z",
      data: {
        user_id: "user_67890",
        username: "john_doe",
        channel_id: "channel_123",
        channel_name: "general",
        joined_at: "2024-01-20T12:30:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "user.left": {
      event: "user.left",
      timestamp: "2024-01-20T12:40:00Z",
      data: {
        user_id: "user_67890",
        username: "john_doe",
        channel_id: "channel_123",
        channel_name: "general",
        left_at: "2024-01-20T12:40:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "user.activity": {
      event: "user.activity",
      timestamp: "2024-01-20T12:38:00Z",
      data: {
        user_id: "user_67890",
        username: "john_doe",
        activity_type: "typing",
        channel_id: "channel_123",
        activity_data: {
          typing: true,
        },
        occurred_at: "2024-01-20T12:38:00Z",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "channel.created": {
      event: "channel.created",
      timestamp: "2024-01-20T12:20:00Z",
      data: {
        channel_id: "channel_456",
        channel_name: "project-discussion",
        channel_type: "public",
        created_by: "user_12345",
        created_at: "2024-01-20T12:20:00Z",
        member_count: 1,
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "channel.deleted": {
      event: "channel.deleted",
      timestamp: "2024-01-20T12:50:00Z",
      data: {
        channel_id: "channel_456",
        channel_name: "old-project",
        deleted_by: "user_12345",
        deleted_at: "2024-01-20T12:50:00Z",
        reason: "Project completed",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "integration.connected": {
      event: "integration.connected",
      timestamp: "2024-01-20T12:00:00Z",
      data: {
        integration_id: "int_789",
        integration_type: "api",
        integration_name: "Customer API",
        connected_at: "2024-01-20T12:00:00Z",
        version: "1.0.0",
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
    "integration.disconnected": {
      event: "integration.disconnected",
      timestamp: "2024-01-20T13:00:00Z",
      data: {
        integration_id: "int_789",
        integration_type: "api",
        integration_name: "Customer API",
        disconnected_at: "2024-01-20T13:00:00Z",
        reason: "Authentication failed",
        error: {
          code: "AUTH_ERROR",
          message: "Invalid API key",
        },
      },
      webhook: {
        id: webhook?.id,
        url: webhook?.url,
      },
    },
  };

  const handleTest = async () => {
    if (!selectedEvent && !useCustomPayload) {
      return;
    }

    if (!user?.id || !webhook?.id) {
      setTestResult({
        success: false,
        error: "Authentication required",
        details: "Please log in to test webhooks",
        timestamp: new Date().toISOString(),
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      let payload;
      if (useCustomPayload) {
        try {
          payload = JSON.parse(customPayload);
        } catch (error) {
          setTestResult({
            success: false,
            error: "Invalid JSON payload",
            details: error.message,
            timestamp: new Date().toISOString(),
          });
          setTesting(false);
          return;
        }
      } else {
        payload = samplePayloads[selectedEvent];
      }

      // Use real webhook service to test the webhook
      const result = await webhookService.testWebhook(
        webhook.id,
        user.id,
        payload,
      );

      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: "Test failed",
        details: error.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setTesting(false);
    }
  };

  const resetForm = () => {
    setSelectedEvent("");
    setCustomPayload("");
    setUseCustomPayload(false);
    setTestResult(null);
  };

  const getCurrentPayload = () => {
    if (useCustomPayload) {
      try {
        return JSON.stringify(JSON.parse(customPayload), null, 2);
      } catch {
        return customPayload;
      }
    }
    if (selectedEvent && samplePayloads[selectedEvent]) {
      return JSON.stringify(samplePayloads[selectedEvent], null, 2);
    }
    return "";
  };

  if (!isOpen || !webhook) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Test Webhook
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Send a test payload to:{" "}
                <code className="bg-muted px-1 rounded">{webhook.url}</code>
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              {/* Left Panel - Configuration */}
              <div className="p-6 border-r border-border space-y-6 overflow-y-auto">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-4">
                    Test Configuration
                  </h3>

                  {/* Payload Type Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="payloadType"
                          checked={!useCustomPayload}
                          onChange={() => setUseCustomPayload(false)}
                          className="accent-primary"
                        />
                        <span className="text-sm text-foreground">
                          Use sample event payload
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="payloadType"
                          checked={useCustomPayload}
                          onChange={() => setUseCustomPayload(true)}
                          className="accent-primary"
                        />
                        <span className="text-sm text-foreground">
                          Custom payload
                        </span>
                      </label>
                    </div>

                    {!useCustomPayload ? (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Select Event Type
                        </label>
                        <select
                          value={selectedEvent}
                          onChange={(e) => setSelectedEvent(e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="">Choose an event...</option>
                          {availableEvents
                            .filter((event) =>
                              webhook.events.includes(event.id),
                            )
                            .map((event) => (
                              <option key={event.id} value={event.id}>
                                {event.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Custom JSON Payload
                        </label>
                        <textarea
                          value={customPayload}
                          onChange={(e) => setCustomPayload(e.target.value)}
                          className="w-full h-40 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 font-mono text-sm"
                          placeholder="Enter your custom JSON payload here..."
                        />
                      </div>
                    )}
                  </div>

                  {/* Test Button */}
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Send"
                    iconPosition="left"
                    onClick={handleTest}
                    disabled={
                      testing ||
                      (!selectedEvent && !useCustomPayload) ||
                      (useCustomPayload && !customPayload.trim())
                    }
                    className="w-full"
                  >
                    {testing ? "Sending Test..." : "Send Test Webhook"}
                  </Button>
                </div>

                {/* Payload Preview */}
                {getCurrentPayload() && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">
                      Payload Preview
                    </h4>
                    <pre className="bg-muted p-3 rounded-md text-xs text-foreground overflow-auto max-h-60 border border-border">
                      {getCurrentPayload()}
                    </pre>
                  </div>
                )}
              </div>

              {/* Right Panel - Results */}
              <div className="p-6 overflow-y-auto">
                <h3 className="text-lg font-medium text-foreground mb-4">
                  Test Results
                </h3>

                {!testResult && !testing && (
                  <div className="text-center py-12">
                    <Icon
                      name="Send"
                      size={48}
                      className="text-muted-foreground mx-auto mb-4"
                    />
                    <p className="text-muted-foreground">
                      Configure and send a test webhook to see results here
                    </p>
                  </div>
                )}

                {testing && (
                  <div className="text-center py-12">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-muted-foreground">
                      Sending test webhook...
                    </p>
                  </div>
                )}

                {testResult && (
                  <div className="space-y-4">
                    {/* Status */}
                    <div
                      className={`p-4 rounded-lg border ${
                        testResult.success
                          ? "bg-accent/10 border-accent/20 text-accent"
                          : "bg-destructive/10 border-destructive/20 text-destructive"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon
                          name={testResult.success ? "CheckCircle" : "XCircle"}
                          size={20}
                        />
                        <span className="font-medium">
                          {testResult.success
                            ? "Test Successful"
                            : "Test Failed"}
                        </span>
                      </div>
                      {testResult.statusCode !== undefined && (
                        <p className="text-sm mt-1">
                          Status Code: {testResult.statusCode}
                        </p>
                      )}
                      {testResult.responseTime !== undefined && (
                        <p className="text-sm">
                          Response Time: {testResult.responseTime}ms
                        </p>
                      )}
                    </div>

                    {/* Error Details */}
                    {!testResult.success && (
                      <div className="bg-muted p-4 rounded-lg border border-border">
                        <h5 className="font-medium text-foreground mb-2">
                          Error Details
                        </h5>
                        <p className="text-sm text-destructive mb-1">
                          {testResult.error}
                        </p>
                        {testResult.details && (
                          <p className="text-xs text-muted-foreground">
                            {testResult.details}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Response */}
                    {testResult.success && testResult.response && (
                      <div className="bg-muted p-4 rounded-lg border border-border">
                        <h5 className="font-medium text-foreground mb-2">
                          Response
                        </h5>
                        <pre className="text-xs text-foreground overflow-auto">
                          {JSON.stringify(testResult.response, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Request Details */}
                    <div className="bg-muted p-4 rounded-lg border border-border">
                      <h5 className="font-medium text-foreground mb-2">
                        Request Details
                      </h5>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">URL:</span>
                          <span className="ml-2 font-mono">
                            {testResult.url}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Method:</span>
                          <span className="ml-2">POST</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Timestamp:
                          </span>
                          <span className="ml-2">
                            {new Date(testResult.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Sent Payload */}
                    <div className="bg-muted p-4 rounded-lg border border-border">
                      <h5 className="font-medium text-foreground mb-2">
                        Sent Payload
                      </h5>
                      <pre className="text-xs text-foreground overflow-auto max-h-60">
                        {JSON.stringify(testResult.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                onClose();
              }}
              disabled={testing}
            >
              Close
            </Button>
            {testResult && (
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => setTestResult(null)}
                disabled={testing}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookTestModal;
